import { OrderScenario } from 'components/order/model/Order';
import { isNameCaptureEnabled } from 'components/settings/model/Settings';
import { CredentialProvider, isPhoneNumberValid } from 'components/user/model/User';
import { useAuth } from 'lib/useAuth';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { ApplicationState } from 'store/store';

export function useContactDetailsState() {
    const { t } = useTranslation();
    const { user, isGuest } = useAuth();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [scenarioFromRoute] = useRouteScenario();
    const scenario = React.useMemo(() => scenarioFromRoute ?? OrderScenario.PREORDER, [scenarioFromRoute]);
    const phoneCaptureEnabled = React.useMemo(
        () => !!settings?.capturePhoneNumberOnOrder && settings?.capturePhoneNumberOnOrder[scenario],
        [scenario, settings?.capturePhoneNumberOnOrder]
    );
    const nameCaptureEnabled = React.useMemo(
        () => isNameCaptureEnabled(settings, scenario),
        [scenario, settings]
    );
    const countryCode = React.useMemo(
        () => settings?.region.phoneNumberCode || 'GB',
        [settings?.region?.phoneNumberCode]
    );

    const [phoneNumber, setPhoneNumber] = React.useState<string | undefined>();
    const [phoneNumberError, setPhoneNumberError] = React.useState<string | undefined>();
    const [phoneNumberTouched, setPhoneNumberTouched] = React.useState<boolean>(false);
    const [firstName, setFirstName] = React.useState<string | undefined>();
    const [lastName, setLastName] = React.useState<string | undefined>();
    const [firstNameTouched, setFirstNameTouched] = React.useState<boolean>(false);
    const [lastNameTouched, setLastNameTouched] = React.useState<boolean>(false);
    const [firstNameError, setFirstNameError] = React.useState<string | undefined>();
    const [lastNameError, setLastNameError] = React.useState<string | undefined>();

    const setPhoneNumberValue = React.useCallback(
        (phoneValue?: string) => {
            if (!phoneNumberTouched) {
                setPhoneNumberTouched(true);
            }
            setPhoneNumber(phoneValue);
        },
        [phoneNumberTouched]
    );

    React.useEffect(() => {
        if (!phoneNumberTouched && !phoneNumber) {
            const { phoneNumberLastUsedToOrder, credentials } = user ?? {};

            const phoneNumberToSet =
                phoneNumberLastUsedToOrder ||
                credentials?.find(c => c.provider === CredentialProvider.MOBILE)?.id;
            if (phoneNumberToSet) {
                setPhoneNumberValue(phoneNumberToSet);
            }
        }
    }, [user, phoneNumberTouched, phoneNumber, setPhoneNumberValue]);

    React.useEffect(() => {
        if (isGuest) {
            return;
        }
        if (!firstNameTouched && !firstName && user?.firstName) {
            setFirstName(user.firstName);
        }
        if (!lastNameTouched && !lastName && user?.lastName) {
            setLastName(user.lastName);
        }
    }, [user, isGuest, firstNameTouched, firstName, lastNameTouched, lastName]);

    const onPhoneNumberChange = React.useCallback(
        (value: string) => {
            const phone = parsePhoneNumberFromString(value, countryCode);
            setPhoneNumberValue(value);
            if (value) {
                if (phone?.isValid()) {
                    setPhoneNumberError('');
                } else {
                    setPhoneNumberError(t('BILL_CHECKOUT_PHONE_NUMBER_INVALID_ERROR'));
                }
            } else {
                setPhoneNumberError(t('BILL_CHECKOUT_PHONE_NUMBER_EMPTY_ERROR'));
            }
        },
        [countryCode, setPhoneNumberValue, t]
    );
    const onFirstNameChange = React.useCallback(
        (value: string) => {
            setFirstNameTouched(true);
            setFirstName(value);
            setFirstNameError(value.trim() ? undefined : t('BILL_CHECKOUT_FIRST_NAME_EMPTY_ERROR'));
        },
        [t]
    );
    const onLastNameChange = React.useCallback(
        (value: string) => {
            setLastNameTouched(true);
            setLastName(value);
            setLastNameError(value.trim() ? undefined : t('BILL_CHECKOUT_LAST_NAME_EMPTY_ERROR'));
        },
        [t]
    );

    const validatePhoneNumber = React.useCallback(() => {
        if (phoneCaptureEnabled) {
            if (!phoneNumber) {
                setPhoneNumberError(t('BILL_CHECKOUT_PHONE_NUMBER_EMPTY_ERROR'));
                return false;
            }
            const isValid = isPhoneNumberValid(phoneNumber, settings?.region.phoneNumberCode);
            if (!isValid) {
                setPhoneNumberError(t('BILL_CHECKOUT_PHONE_NUMBER_INVALID_ERROR'));
                return false;
            }
        }
        return true;
    }, [phoneCaptureEnabled, phoneNumber, settings?.region.phoneNumberCode, t]);
    const validateContactDetails = React.useCallback(() => {
        const phoneNumberValid = validatePhoneNumber();
        let namesValid = true;
        if (nameCaptureEnabled) {
            if (!firstName?.trim()) {
                setFirstNameError(t('BILL_CHECKOUT_FIRST_NAME_EMPTY_ERROR'));
                namesValid = false;
            }
            if (!lastName?.trim()) {
                setLastNameError(t('BILL_CHECKOUT_LAST_NAME_EMPTY_ERROR'));
                namesValid = false;
            }
        }
        return phoneNumberValid && namesValid;
    }, [firstName, lastName, nameCaptureEnabled, t, validatePhoneNumber]);

    return {
        phoneNumber,
        phoneNumberError,
        onPhoneNumberChange,
        phoneCaptureEnabled,
        firstName,
        lastName,
        firstNameError,
        lastNameError,
        onFirstNameChange,
        onLastNameChange,
        nameCaptureEnabled,
        validateContactDetails
    };
}
